import React, { useCallback, useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useNavigate } from 'react-router'
import { useService } from '@evertel/di'
import { AgreementGateController } from '@evertel/agreements'
import { AgreementGate } from '@evertel/web/agreements'
import { CurrentUserController } from '@evertel/blue-user'
import { DepartmentController } from '@evertel/department'
import { RoomController } from '@evertel/room'
import { useUI } from '@evertel/web/ui'
import { ReportingDepartmentGate } from './ReportingDepartmentGate'
import { RoomLoadingState } from './elements/RoomLoadingState'

interface RoomGateProps {
    roomId: number
    onComplete: () => void
}

const RoomGate: React.FC<RoomGateProps> = observer(({
    roomId,
    onComplete
}) => {
    const roomController = useService(RoomController, [roomId])
    const agreementGateController = useService(AgreementGateController, [roomId])
    const currentUserController = useService(CurrentUserController, [])
    const { addToast } = useUI()
    const navigate = useNavigate()

    const room = roomController.room
    const roomDepartmentId = room?.departmentId
    const departmentController = useService(DepartmentController, [roomDepartmentId])

    const [isLoading, setIsLoading] = useState(true)
    const [isAccepting, setIsAccepting] = useState(false)

    const load = useCallback(async () => {
        setIsLoading(true)
        try {
            await agreementGateController.init(roomId)
        } catch (e: any) {
            addToast({
                color: 'danger',
                title: 'Could not open room',
                message: e?.message || 'Failed to check agreements. Please try again.'
            })
            navigate('/landing', { replace: true })
        } finally {
            setIsLoading(false)
        }
    }, [roomId, agreementGateController, addToast, navigate])

    useEffect(() => {
        roomController.init(roomId)
    }, [roomId, roomController])

    useEffect(() => {
        load()
    }, [load])

    useEffect(() => {
        if (roomDepartmentId) departmentController.init(roomDepartmentId)
    }, [roomDepartmentId])

    const isGuestInRoomDepartment = currentUserController.isGuestInDepartment(roomDepartmentId)
    const hasNoMemberships = !currentUserController.departmentsAccessNotGuest?.length
    const hasNoReportingDepartment = !currentUserController.reportingDepartment
    const showReportingAgencyGate = isGuestInRoomDepartment && hasNoMemberships && hasNoReportingDepartment

    const totalSteps = agreementGateController.totalCount + (showReportingAgencyGate ? 1 : 0)
    const overallStep = agreementGateController.hasPendingAgreements
        ? agreementGateController.currentStep
        : agreementGateController.totalCount + 1

    let activeGate: 'agreements' | 'reportingDepartment' | null = null
    if (agreementGateController.hasPendingAgreements) activeGate = 'agreements'
    else if (showReportingAgencyGate) activeGate = 'reportingDepartment'

    useEffect(() => {
        if (!isLoading && roomDepartmentId !== undefined && activeGate === null) onComplete()
    }, [isLoading, roomDepartmentId, activeGate, onComplete])

    // Wait for the room's departmentId before deciding gates: the reporting
    // gate depends on it, so completing while it's still undefined could skip it.
    if (isLoading || roomDepartmentId === undefined) {
        return <RoomLoadingState variant="full" />
    }

    if (activeGate === 'agreements') {
        const currentDoc = agreementGateController.currentAgreement?.document
        return (
            <div className="m-4">
                <AgreementGate
                    key={agreementGateController.currentAgreement?.departmentAgreementId}
                    content={currentDoc?.content ?? ''}
                    roomName={room?.name}
                    roomDescription={room?.description}
                    departmentId={roomDepartmentId}
                    departmentName={departmentController.department?.name}
                    agreementName={agreementGateController.currentAgreement?.document?.name}
                    effectiveDate={currentDoc?.createdDate}
                    version={currentDoc?.version}
                    currentStep={totalSteps > 1 ? overallStep : undefined}
                    totalSteps={totalSteps > 1 ? totalSteps : undefined}
                    isAccepting={isAccepting}
                    onAccept={async () => {
                        setIsAccepting(true)
                        try {
                            await agreementGateController.accept()
                        } catch (e) {
                            addToast({
                                color: 'danger',
                                title: 'Acceptance Failed',
                                message: 'Failed to record acceptance. Please try again.'
                            })
                        } finally {
                            setIsAccepting(false)
                        }
                    }}
                />
            </div>
        )
    }

    if (activeGate === 'reportingDepartment') {
        return (
            <div className="m-4">
                <ReportingDepartmentGate
                    roomName={room?.name}
                    roomDescription={room?.description}
                    currentStep={totalSteps > 1 ? overallStep : undefined}
                    totalSteps={totalSteps > 1 ? totalSteps : undefined}
                    onSave={(departmentName, position) =>
                        currentUserController.updateReportingDepartment(position, departmentName)
                    }
                />
            </div>
        )
    }

    return null
})

RoomGate.displayName = 'RoomGate'
export { RoomGate }
